import { api } from '../api.js';
import { openPlaidLink } from '../plaid-link.js';
import {
  h, money, moneyCents, compact, changeLine, isDebt, getRange, rangeLong, rangeSwitch, sparkline, lineChart,
  accountChange, shortDate, monthName, section, pair, targetBar, type Range, type SeriesPoint,
} from '../ui.js';

// Home, top to bottom: net and its trend; this month (spent vs the target and vs
// last month, the month in one line, paycheck, big purchases); cards (what's owed,
// the pace, the 0% deadline); recurring; one row per account. A broken bank
// connection gets one line at the very top with a Fix button.

interface OverviewAccount {
  id: number; name: string; type: string; institution: string; manual: boolean;
  in_net: boolean; balance: number; change: number; series: SeriesPoint[];
  market?: { from: string; added: number; market: number } | null;
}
interface BigPurchase { id: number; date: string; description: string; amount: number; account: string }
interface Overview {
  range: Range; start: string; today: string;
  net: { value: number; change: number; series: SeriesPoint[] };
  spending: { this_month: number; last_month_same_point: number; last_month: number; same_point_date: string; target: number | null };
  month: { month: string; in: number; spent: number; debt_paid_down: number; invested: number };
  paycheck: { payer: string; latest: number; date: string; usual: number; change: number } | null;
  big_purchases: BigPurchase[];
  debt: {
    owed: number; since: string; change_since: number; pace_per_month: number; clears: string | null;
    interest_this_month: number;
    warnings: { id: number; name: string; ends: string; needed_per_month: number; pace_per_month: number }[];
    missing_promo: { id: number; name: string }[];
  } | null;
  recurring: { subscriptions_monthly: number; subscriptions_count: number; recurring_monthly: number };
  accounts: OverviewAccount[];
  attention: { id: number; label: string; issue: string }[];
}

const go = (hash: string) => () => { location.hash = hash; };

export function homeScreen(): HTMLElement {
  const root = h('div', 'screen');
  let range = getRange();

  const top = h('div', 'topbar');
  top.appendChild(h('div', 'wordmark', 'CoinHealth'));

  const body = h('div');
  body.appendChild(h('div', 'loading', 'Loading…'));
  root.append(top, body);

  async function load(): Promise<void> {
    try {
      render(await api.get<Overview>(`/overview?range=${range}`));
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') return;
      body.innerHTML = '';
      body.appendChild(h('div', 'error', err instanceof Error ? err.message : 'Could not load'));
    }
  }

  function render(d: Overview): void {
    body.innerHTML = '';

    for (const c of d.attention) {
      const banner = h('div', 'banner');
      const fix = h('button', 'btn small', 'Fix');
      fix.addEventListener('click', async () => {
        fix.disabled = true;
        await openPlaidLink({ connectionId: c.id, onLinked: load, onError: (m) => alert(m) });
        fix.disabled = false;
      });
      banner.append(h('span', '', `${c.label} ${c.issue}.`), fix);
      body.appendChild(banner);
    }

    body.append(
      h('div', 'eyebrow', 'Net total'),
      h('div', 'money hero', money(d.net.value)),
      changeLine(d.net.change, rangeLong(range)),
      lineChart(d.net.series, { height: 160 }),
      rangeSwitch(range, (r) => { range = r; void load(); }),
    );

    body.appendChild(monthSection(d));
    if (d.debt) body.appendChild(debtSection(d.debt));
    body.appendChild(recurringSection(d.recurring));

    const accounts = section('Accounts');
    for (const a of d.accounts) accounts.body.appendChild(accountRow(a));
    if (d.accounts.length === 0) {
      const link = h('button', 'link', 'Link a bank in Settings ›');
      link.addEventListener('click', go('#settings'));
      accounts.body.appendChild(link);
    }
    body.appendChild(accounts.el);
  }

  function monthSection(d: Overview): HTMLElement {
    const s = section(monthName(d.month.month).split(' ')[0], { label: 'Budget', onClick: go('#budget') });
    s.body.appendChild(pair([
      { label: 'Spent so far', value: money(d.spending.this_month) },
      { label: `By ${shortDate(d.spending.same_point_date)}`, value: money(d.spending.last_month_same_point) },
    ]));

    if (d.spending.target) {
      const [y, m, day] = d.today.split('-').map(Number);
      const bar = targetBar(d.spending.this_month, d.spending.target, day, new Date(y, m, 0).getDate());
      bar.addEventListener('click', go('#budget'));
      s.body.appendChild(bar);
    } else {
      const set = h('button', 'link', 'Set a monthly spending target ›');
      set.addEventListener('click', go('#budget'));
      s.body.appendChild(set);
    }

    // The month in one line: where the money came from and went.
    const m = d.month;
    const line = h('div', 'month-line');
    const part = (label: string, value: string, cls = '') => {
      const span = h('span', cls);
      span.append(h('span', 'muted', label + ' '), h('span', 'money', value));
      return span;
    };
    line.append(
      part('In', compact(m.in)),
      part('Spent', compact(m.spent)),
      part('Cards', `${m.debt_paid_down >= 0 ? '−' : '+'}${compact(Math.abs(m.debt_paid_down))}`, m.debt_paid_down >= 0 ? 'up' : 'down'),
      part('Invested', compact(m.invested)),
    );
    s.body.appendChild(line);

    if (d.paycheck) {
      const p = d.paycheck;
      s.body.appendChild(h('div', p.change > 0 ? 'note up' : 'note', p.change
        ? `Paycheck ${money(p.latest)}, ${p.change > 0 ? 'up' : 'down'} ${money(Math.abs(p.change))} since ${shortDate(p.date)}.`
        : `Paycheck ${money(p.latest)} on ${shortDate(p.date)}.`));
    }

    if (d.big_purchases.length) {
      s.body.appendChild(h('div', 'label sub-head', 'Big purchases'));
      for (const b of d.big_purchases.slice(0, 3)) {
        const row = h('div', 'txn');
        const desc = h('div', 'txn-desc');
        desc.append(h('span', '', b.description), h('span', 'tag', b.account.split(' ')[0]));
        row.append(h('div', 'txn-date', shortDate(b.date)), desc, h('div', 'txn-amt', moneyCents(b.amount)));
        s.body.appendChild(row);
      }
    }
    return s.el;
  }

  function debtSection(debt: NonNullable<Overview['debt']>): HTMLElement {
    const s = section('Cards');
    const cells: { label: string; value: string; cls?: string }[] = [{ label: 'Owed', value: money(debt.owed) }];
    if (Math.round(debt.change_since) !== 0) {
      cells.push({
        label: `Since ${shortDate(debt.since)}`,
        value: `${debt.change_since > 0 ? '−' : '+'}${money(Math.abs(debt.change_since))}`,
        cls: debt.change_since > 0 ? 'up' : 'down',
      });
    }
    s.body.appendChild(pair(cells));
    s.body.appendChild(h('div', 'note', debt.clears
      ? `Paid off by ${monthName(debt.clears)} at ${compact(debt.pace_per_month)} a month.`
      : debt.owed > 0 ? 'Not going down over the last 3 months.' : 'Nothing owed.'));
    if (debt.interest_this_month > 0) s.body.appendChild(h('div', 'note', `${moneyCents(debt.interest_this_month)} interest this month.`));

    for (const w of debt.warnings) {
      const warn = h('button', 'banner');
      warn.appendChild(h('span', '', `${w.name}: 0% ends ${shortDate(w.ends)} ${w.ends.slice(0, 4)}. `
        + `At ${compact(Math.max(0, w.pace_per_month))} a month it won't be paid off in time; about ${money(w.needed_per_month)} a month would do it.`));
      warn.addEventListener('click', go(`#account?id=${w.id}`));
      s.body.appendChild(warn);
    }
    for (const c of debt.missing_promo) {
      const ask = h('button', 'link', `${c.name} charges no interest. Set its 0% end date ›`);
      ask.addEventListener('click', go(`#account?id=${c.id}`));
      s.body.appendChild(ask);
    }
    return s.el;
  }

  function recurringSection(r: Overview['recurring']): HTMLElement {
    const s = section('Recurring', { label: 'All', onClick: go('#recurring') });
    const p = pair([
      { label: `${r.subscriptions_count} subscriptions, a month`, value: money(r.subscriptions_monthly) },
      { label: 'Other bills, a month', value: money(r.recurring_monthly) },
    ]);
    p.classList.add('tappable');
    p.addEventListener('click', go('#recurring'));
    s.body.appendChild(p);
    return s.el;
  }

  function accountRow(a: OverviewAccount): HTMLElement {
    const row = h('button', 'acct');
    row.addEventListener('click', go(`#account?id=${a.id}`));

    const left = h('div');
    left.style.minWidth = '0';
    left.append(h('div', 'acct-name', a.name), h('div', 'sub', a.manual ? 'Manual' : a.institution));

    const right = h('div', 'acct-right');
    const bal = h('div', 'money acct-bal', money(isDebt(a.type) ? -a.balance : a.balance));
    if (isDebt(a.type)) bal.appendChild(h('span', 'owed', 'owed'));
    right.appendChild(bal);
    if (a.market) {
      // A brokerage's change is mostly your own deposits: show what the market did.
      const m = a.market.market;
      right.appendChild(h('div', `acct-chg ${Math.round(m) >= 0 ? 'up' : 'down'}`, `market ${m >= 0 ? '+' : '−'}${money(Math.abs(m))}`));
    } else {
      right.appendChild(accountChange(a.type, a.change));
    }
    if (!a.in_net) right.appendChild(h('div', 'acct-chg muted', 'not in net'));

    row.append(left, sparkline(a.series), right);
    return row;
  }

  // A background sync landed: refresh the numbers in place.
  const onSynced = () => { if (root.isConnected) void load(); else window.removeEventListener('ch-synced', onSynced); };
  window.addEventListener('ch-synced', onSynced);

  void load();
  return root;
}
